import { useEffect, useMemo, useState } from "react";

import { occupationLabel } from "@core";
import type { EmployerView } from "../lib/employer";
import type { UnworkedHoliday } from "@core";
import { loadRegister, type RegisterMonth, type RegisterRow, type RegisterStatus } from "../lib/register";

/**
 * The attendance register: one person, one month, in the form you would hand
 * over if somebody asked to see it.
 *
 * This screen is also the website's `#records` illustration — the redesign's
 * "Attendance overview" panel was drawn before the dashboard existed, and the
 * plan is to replace the drawing with a screenshot of this. So it is meant to
 * look like that: KPI tiles across the top, then DATE / CLOCK IN / CLOCK OUT /
 * HOURS / STATUS with a pill in the last column.
 */

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const STATUS: Record<RegisterStatus, { label: string; tone: string; why?: string }> = {
  complete: { label: "Complete", tone: "ok" },
  open: { label: "On the clock", tone: "live", why: "Still clocked in — these hours are not counted yet." },
  "auto-closed": {
    label: "Auto-closed",
    tone: "warn",
    why: "Nobody tapped out. The leaving time is a guess from the roster, not a recorded time.",
  },
  flagged: { label: "Needs a look", tone: "warn", why: "This shift was flagged when it was recorded." },
  "overtime-undecided": {
    label: "Overtime to decide",
    tone: "warn",
    why: "Overtime nobody has answered yet. The hours are owed either way.",
  },
};

function hoursLabel(value: number): string {
  const whole = Math.floor(value);
  const mins = Math.round((value - whole) * 60);
  return mins === 0 ? `${whole}h` : `${whole}h ${String(mins).padStart(2, "0")}m`;
}

function dayLabel(iso: string, timeZone: string): string {
  // Noon avoids the date slipping a day when the zone offset is applied.
  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(`${iso}T12:00:00Z`));
}

function ShiftRow({ row, tz }: { row: RegisterRow; tz: string }) {
  const status = STATUS[row.status];
  return (
    <tr>
      <td style={{ paddingLeft: 22 }} className="num">{dayLabel(row.date, tz)}</td>
      <td className="num">{row.clockIn}</td>
      <td className="num">{row.clockOut ?? "—"}</td>
      <td className="num">{row.clockOut ? hoursLabel(row.hours) : "—"}</td>
      <td className="num">{row.overtime > 0 ? hoursLabel(row.overtime) : "—"}</td>
      <td style={{ paddingRight: 22 }}>
        <span className={`pill ${status.tone}`} title={status.why ?? ""}>{status.label}</span>
      </td>
    </tr>
  );
}

/**
 * A public holiday that was paid and not worked.
 *
 * Deliberately unlike a shift: no clock times, no hours, because none of those
 * happened. Nothing is invented to fill the columns — an em dash is the honest
 * answer to "what time did they arrive" on a day they were entitled to stay
 * home. What the row says instead is why it is there at all.
 */
function HolidayRow({ entry, tz }: { entry: UnworkedHoliday; tz: string }) {
  return (
    <tr className="holiday">
      <td style={{ paddingLeft: 22 }} className="num">
        {dayLabel(entry.holiday.date, tz)}
        <div className="panel-note">{entry.holiday.name}</div>
      </td>
      <td className="num">—</td>
      <td className="num">—</td>
      <td className="num">—</td>
      <td className="num">—</td>
      <td style={{ paddingRight: 22 }}>
        <span className="pill violet" title="A public holiday they would ordinarily have worked. BCEA s18(2)(a) pays for it whether or not they came in.">
          Paid, not worked
        </span>
      </td>
    </tr>
  );
}

export function Register({ ctx }: { ctx: EmployerView }) {
  const people = useMemo(() => ctx.employees, [ctx.employees]);
  const [employeeId, setEmployeeId] = useState(people[0]?.id ?? "");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<RegisterMonth | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!employeeId) return;
    let cancelled = false;
    setLoading(true);
    loadRegister(employeeId, year, month, ctx.timezone).then((result) => {
      if (cancelled) return;
      setData(result);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [employeeId, year, month, ctx.timezone]);

  const person = people.find((p) => p.id === employeeId);
  const money = new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: ctx.currency,
    maximumFractionDigits: 0,
  });

  if (people.length === 0) {
    return (
      <div className="panel">
        <div className="empty">
          There is nobody to show a register for yet.
          <br />
          Add somebody on the People page first.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Attendance register</h1>
          <p>
            The record of arrivals and departures South African law requires an employer to keep.
            Times are {ctx.timezone.replace("_", " ")}.
          </p>
        </div>
      </div>

      <div className="panel">
        <div className="panel-top">
          <div className="panel-title">Attendance overview</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <select
              className="compact"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              aria-label="Person"
            >
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.fullName}
                </option>
              ))}
            </select>
            <select
              className="compact"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              aria-label="Month"
            >
              {MONTHS.map((name, index) => (
                <option key={name} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
            <select
              className="compact"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              aria-label="Year"
            >
              {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="panel-body">
          <div className="kpis">
            <div className="kpi">
              <small>Hours recorded</small>
              <strong>{hoursLabel(data?.hours ?? 0)}</strong>
            </div>
            <div className="kpi">
              <small>Shifts completed</small>
              <strong>{data?.completed ?? 0}</strong>
            </div>
            <div className="kpi">
              <small>Overtime</small>
              <strong>{hoursLabel(data?.overtime ?? 0)}</strong>
            </div>
            <div className="kpi">
              <small>Wages</small>
              <strong>{money.format(data?.wages ?? 0)}</strong>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="empty">Reading the register…</div>
        ) : !data || (data.rows.length === 0 && data.holidays.length === 0) ? (
          <div className="empty">
            No shifts recorded for {person?.fullName ?? "this person"} in {MONTHS[month - 1]} {year}.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="rows">
              <thead>
                <tr>
                  <th style={{ paddingLeft: 22 }}>Date</th>
                  <th>Clock in</th>
                  <th>Clock out</th>
                  <th>Hours</th>
                  <th>Overtime</th>
                  <th style={{ paddingRight: 22 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {/* Shifts and paid public holidays in one sequence, newest
                    first, so a holiday sits on its own date rather than in a
                    separate list the reader has to reconcile by eye. */}
                {[
                  ...data.rows.map((row) => ({ date: row.date, node: <ShiftRow key={row.id} row={row} tz={ctx.timezone} /> })),
                  ...data.holidays.map((h) => ({
                    date: h.holiday.date,
                    node: <HolidayRow key={`h-${h.holiday.date}`} entry={h} tz={ctx.timezone} />,
                  })),
                ]
                  .sort((a, b) => b.date.localeCompare(a.date))
                  .map((r) => r.node)}
              </tbody>
            </table>
          </div>
        )}

        <div className="panel-foot">
          {person
            ? `${person.fullName} — ${occupationLabel(person.occupation, person.occupationOther)}`
            : ""}
          {data && data.rows.some((r) => r.status === "auto-closed")
            ? " · Auto-closed shifts carry a leaving time guessed from the roster, not a recorded one."
            : ""}
        </div>
      </div>
    </>
  );
}
